<template>
  <div v-cloak class="diet-detail" :style="dietDetailStyle">
    <DietDetailHeader
      :diet-id="dietId"
      :diet-type="dietType"
      :is-generating="isGeneratingDietDays"
      :is-mounted="isMounted"
      @refresh="onDietActionRefresh"
      @select="onSelectActiveDiet"
    />
    <DietDetailNotices />
    <div class="diet-detail__navigation">
      <DietDetailNavigation :diet-id="dietId" :diet-type="dietType" />
      <DietDetailActionBar :diet-id="dietId" :diet-type="dietType" />
    </div>
    <DietDetailScheduleToolbar />
    <router-view />
  </div>
</template>

<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { computed } from 'vue'

  import DietDetailScheduleToolbar from '@/components/diets/DietDetailScheduleToolbar.vue'
  import {
    DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX,
    DIET_AVERAGE_COLUMN_WIDTH_PX,
    DIET_WEEK_DAYS_COUNT,
  } from '@/const'
  import { useDietsStore } from '@/store/dietsStore'
  import { getDietDaysCount } from '@/utils/dietDays'

  import DietDetailActionBar from './components/DietDetailActionBar/DietDetailActionBar.vue'
  import DietDetailHeader from './components/DietDetailHeader/DietDetailHeader.vue'
  import DietDetailNavigation from './components/DietDetailNavigation/DietDetailNavigation.vue'
  import DietDetailNotices from './components/DietDetailNotices/DietDetailNotices.vue'
  import { useDietDetail } from './composables/useDietDetail'

  const {
    dietId,
    dietType,
    isGeneratingDietDays,
    isMounted,
    onDietActionRefresh,
    onSelectActiveDiet,
    showAllDays,
  } = useDietDetail()

  const { averageActive, diet } = storeToRefs(useDietsStore())
  const dietDetailStyle = computed(() => {
    const daysCount = getDietDaysCount(diet.value)
    const averageColumnWidthPx = averageActive.value
      ? DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX
      : DIET_AVERAGE_COLUMN_WIDTH_PX
    const averageColumnStyle = {
      '--diet-average-column-width': `${averageColumnWidthPx}px`,
    }
    if (!showAllDays.value || daysCount <= DIET_WEEK_DAYS_COUNT) return averageColumnStyle

    const visibleWeeks = daysCount / DIET_WEEK_DAYS_COUNT

    return {
      ...averageColumnStyle,
      '--diet-detail-viewport-width': `calc(${100 / visibleWeeks}% + ${averageColumnWidthPx}px)`,
      width: `calc(${visibleWeeks * 100}% - ${visibleWeeks * averageColumnWidthPx}px)`,
    }
  })
</script>

<style scoped>
  .diet-detail {
    width: 100%;
    min-width: 0;
  }

  .diet-detail > :not(.week, .comments--all-days) {
    position: sticky;
    left: 0;
    box-sizing: border-box;
    margin-right: calc(-1 * var(--diet-average-column-width, 0px));
    width: var(--diet-detail-viewport-width, 100%);
  }

  .diet-detail__navigation {
    display: flex;
    flex-direction: row;
    z-index: 6;
  }
</style>
